import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import {
	createElement,
	isValidElement,
	type CSSProperties,
	type ReactNode,
} from "react";
// eslint-disable-next-line no-restricted-imports -- the only sanctioned wrapper around sonner's toast
import { ExternalToast, toast as sonnerToast } from "sonner";
import {
	ToastActionButton,
	ToastKind,
	ToastView,
} from "./components/toast-view";

/**
 * Every toast renders through `ToastView`: neutral toasts (default, message, loading) as a
 * Snackbar, severity toasts as an `Alert`. Sonner only stacks, times and swipes them.
 *
 * Toast persistence rules:
 * - Non-actionable success / info / message toasts auto-dismiss, with a close button to
 *   dismiss early and a draining progress bar (rendered by `AppToaster`).
 * - Actionable toasts (with `action` or `cancel`), warnings and errors persist until
 *   dismissed and show a close button.
 * - Loading toasts persist until they are updated or dismissed, and show a close button
 *   only with `closeButton: true`.
 * - `persist: false` keeps an error or warning timed (input-guard feedback such as
 *   "Exceeded available quantity"); `persist: true` forces persistence.
 * An explicit `duration` or `closeButton` always wins.
 */
export interface AppToastOptions extends ExternalToast {
	persist?: boolean;
}

type ToastMessage = ReactNode | (() => ReactNode);

/** Auto-close duration shared by the wrapper and `AppToaster`. */
export const TOAST_DURATION_MS = 4000;
/** Marks auto-closing toasts so `AppToaster` renders their countdown bar. */
export const AUTO_CLOSE_TOAST_CLASS = "served-toast-auto-close";
export const AUTO_CLOSE_DURATION_VAR = "--served-toast-auto-close-duration";
export const AUTO_CLOSE_ANIMATION_VAR = "--served-toast-auto-close-animation";
/** Two identical countdown animations; switching between them restarts the bar. */
export const AUTO_CLOSE_ANIMATIONS = [
	"served-toast-auto-close-0",
	"served-toast-auto-close-1",
] as const;

/** 0 or 1: alternates on every re-trigger of the same toast id. */
type AutoCloseCycle = 0 | 1;

// Explicit undefined values reset options merged from an earlier toast with the same id.
const PERSISTENT_OPTIONS = {
	duration: Infinity,
	closeButton: true,
	className: undefined,
};

const isActionable = (options: ExternalToast) =>
	options.action !== undefined || options.cancel !== undefined;

const getDedupeId = (message: ToastMessage, type: string) =>
	typeof message === "string" ? `persistent:${type}:${message}` : undefined;

export const resolveToastOptions = (
	type: "default" | "success" | "info" | "message" | "warning" | "error",
	message: ToastMessage,
	options: AppToastOptions = {},
	cycle: AutoCloseCycle = 0,
): ExternalToast => {
	const { persist, ...rest } = options;
	const persistsByType = type === "warning" || type === "error";
	const shouldPersist = persist ?? (persistsByType || isActionable(rest));

	if (!shouldPersist) {
		// Re-triggering a toast by id updates it in place. Changing the duration (by 1ms)
		// makes sonner restart its timer from full, and switching the animation restarts
		// the bar, so both start over together.
		const duration = (rest.duration ?? TOAST_DURATION_MS) + cycle;
		return {
			...rest,
			duration,
			closeButton: rest.closeButton ?? true,
			className: [AUTO_CLOSE_TOAST_CLASS, rest.className]
				.filter(Boolean)
				.join(" "),
			style: {
				[AUTO_CLOSE_DURATION_VAR]: `${duration}ms`,
				[AUTO_CLOSE_ANIMATION_VAR]: AUTO_CLOSE_ANIMATIONS[cycle],
				...rest.style,
			} as CSSProperties,
		};
	}

	// Identical persistent toasts collapse into one instead of stacking.
	return {
		...PERSISTENT_OPTIONS,
		id: getDedupeId(message, type),
		...rest,
	};
};

const autoCloseCycles = new Map<string | number, AutoCloseCycle>();

const nextAutoCloseCycle = (id?: string | number): AutoCloseCycle => {
	// Toasts without an explicit id are always new toasts, so nothing to restart.
	if (id === undefined) return 0;
	const cycle = autoCloseCycles.get(id) === 1 ? 0 : 1;
	autoCloseCycles.set(id, cycle);
	return cycle;
};

const resolveNode = (node: ToastMessage) =>
	typeof node === "function" ? node() : node;

/** Renders the toast with MUI; props only `ToastView` renders stay out of sonner. */
const show = (
	kind: ToastKind,
	message: ToastMessage,
	{ description, icon, action, cancel, ...options }: ExternalToast,
) =>
	sonnerToast.custom(
		(id) =>
			createElement(ToastView, {
				kind,
				message: resolveNode(message),
				description: resolveNode(description),
				icon,
				action,
				cancel,
				closeButton: options.closeButton,
				onDismiss: () => sonnerToast.dismiss(id),
			}),
		options,
	);

const withRules =
	(type: Parameters<typeof resolveToastOptions>[0]) =>
	(message: ToastMessage, options?: AppToastOptions) =>
		show(
			type,
			message,
			resolveToastOptions(
				type,
				message,
				options,
				nextAutoCloseCycle(options?.id),
			),
		);

const loading = (message: ToastMessage, options?: ExternalToast) =>
	show("loading", message, {
		...PERSISTENT_OPTIONS,
		closeButton: false,
		...options,
	});

/** Mirrors sonner's extended promise result: toast options plus the message. */
type PromiseExtendedResult = AppToastOptions & { message: ReactNode };

type PromiseResult<Value> =
	| ReactNode
	| PromiseExtendedResult
	| ((
			value: Value,
	  ) =>
			| ReactNode
			| PromiseExtendedResult
			| Promise<ReactNode | PromiseExtendedResult>);

export interface AppToastPromiseData<Value> extends AppToastOptions {
	loading: ReactNode;
	success?: PromiseResult<Value>;
	error?: PromiseResult<Error>;
	finally?: () => void | Promise<void>;
}

const resolvePromiseResult = async <Value>(
	result: PromiseResult<Value>,
	value: Value,
): Promise<PromiseExtendedResult> => {
	const resolved = typeof result === "function" ? await result(value) : result;
	const isExtendedResult =
		typeof resolved === "object" &&
		resolved !== null &&
		!isValidElement(resolved) &&
		"message" in resolved;
	return isExtendedResult ? resolved : { message: resolved };
};

/**
 * Shows a loading toast that turns into a success or (persistent) error toast.
 * An omitted `success` or `error` dismisses the toast instead, as sonner does.
 */
const promise = <Value>(
	promiseValue: Promise<Value> | (() => Promise<Value>),
	{
		loading: loadingMessage,
		success,
		error,
		finally: onFinally,
		...options
	}: AppToastPromiseData<Value>,
) => {
	const id = loading(loadingMessage, {
		id: options.id,
		description: options.description,
	});
	const settle = async <Result>(
		result: PromiseResult<Result> | undefined,
		value: Result,
		notify: typeof toast.success,
	) => {
		if (result === undefined) {
			sonnerToast.dismiss(id);
			return;
		}
		const { message, ...rest } = await resolvePromiseResult(result, value);
		notify(message, { ...options, ...rest, id });
	};

	void (typeof promiseValue === "function" ? promiseValue() : promiseValue)
		.then(
			(value) => settle(success, value, toast.success),
			(reason: Error) => settle(error, reason, toast.error),
		)
		.finally(() => onFinally?.());

	return id;
};

export const toast = Object.assign(withRules("default"), {
	success: withRules("success"),
	info: withRules("info"),
	message: withRules("message"),
	warning: withRules("warning"),
	error: withRules("error"),
	loading,
	promise,
	custom: sonnerToast.custom,
	dismiss: sonnerToast.dismiss,
	getHistory: sonnerToast.getHistory,
	getToasts: sonnerToast.getToasts,
});

/** "Copy Ref" toast action: copies a request reference and confirms it was copied. */
export const copyRefAction = (
	reference: string,
	copy: (text: string) => Promise<unknown> = (text) =>
		navigator.clipboard.writeText(text),
): ToastActionButton => ({
	label: "Copy Ref",
	confirmation: "Copied",
	onClick: () => copy(reference),
});

export const systemStatusDescription = (reference?: string): ReactNode =>
	createElement(
		Stack,
		{ spacing: 0.5 },
		reference ? createElement("span", null, `Ref: ${reference}`) : null,
		createElement(
			Link,
			{
				href: process.env.SYSTEM_STATUS_URL,
				target: "_blank",
				rel: "noopener noreferrer",
				color: "inherit",
				underline: "always",
			},
			"System status",
		),
	);

export const errorToastOptions = (
	isServedFault: boolean,
	reference?: string,
	copyReference?: (reference: string) => Promise<unknown>,
) =>
	(isServedFault && process.env.SYSTEM_STATUS_URL) || reference
		? {
				description:
					isServedFault && process.env.SYSTEM_STATUS_URL
						? systemStatusDescription(reference)
						: `Ref: ${reference}`,
				action: reference ? copyRefAction(reference, copyReference) : undefined,
			}
		: undefined;
